import { action, set } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { CardStandard } from "@shared/models/card-stack"
import type { Permission } from "@shared/models/permissions"
import { permissionsInclude } from "cc-frontend/helpers/permissions-include"
import lessonStandardsCopyAction from "cc-frontend/lib/actions/lesson/LESSON_STANDARDS_COPY"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type Session from "cc-frontend/services/session"
import { find, isArray, isNil } from "lodash-es"
import styles from "./standard.module.css"

interface Signature {
  Args: {
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    cardStackId: string
    cardId: string
    standards: Array<CardStandard>
    standard: CardStandard
    permissions: Array<Permission>
  }
}

export default class StandardComponent extends Component<Signature> {
  readonly styles = styles

  @service declare session: Session
  @service declare dispatcher: Dispatcher
  @service declare lessonEditor: LessonEditor

  @action copyStandards(standards) {
    if (isNil(standards)) return
    if (this.session.isAuthenticated !== true) {
      this.session.mustSignInToProceed("Copy Standards")
      return
    }
    if (!isArray(standards)) standards = [standards]
    if (this.session.userId !== null) {
      this.dispatcher.dispatchPreparedAction(
        lessonStandardsCopyAction({
          currentUserId: this.session.userId,
          standards: standards,
          cardStackId: this.args.cardStackId,
        })
      )
    }
  }

  @action deleteStandard(standard) {
    if (permissionsInclude(this.args.permissions, "can-edit-plan") === false) return
    this.lessonEditor.deleteStandard(
      this.args.modelsNeededForFirstEdit,
      this.args.cardStackId,
      this.args.cardId,
      standard
    )
  }

  @action toggleStar(standards, standard_id) {
    if (permissionsInclude(this.args.permissions, "can-edit-plan") === false) return
    let standard = find(standards, (s) => s.id == standard_id)
    if (standard.emphasisLevel === "STARRED") {
      standard.emphasisLevel = null
    } else {
      standard.emphasisLevel = "STARRED"
    }

    this.lessonEditor.toggleStandardEmphasisLevel(
      this.args.modelsNeededForFirstEdit,
      this.args.cardStackId,
      this.args.cardId,
      standard.emphasisLevel,
      standards
    )
  }

  @action toggleExpansion(standards, standard_id) {
    let standard = find(standards, (s) => s.id == standard_id)
    // Don't let clicking on a standard change the starred status
    if (standard.emphasisLevel === "STARRED") return

    // Always set locally so that viewers can click standards to expand them.
    if (standard.emphasisLevel === "EXPANDED") {
      set(standard, "emphasisLevel", null)
    } else {
      set(standard, "emphasisLevel", "EXPANDED")
    }

    // If permitted, also sync the change.
    if (permissionsInclude(this.args.permissions, "can-edit-plan")) {
      this.lessonEditor.toggleStandardEmphasisLevel(
        this.args.modelsNeededForFirstEdit,
        this.args.cardStackId,
        this.args.cardId,
        standard.emphasisLevel,
        standards
      )
    }
  }
}
